import React, { useEffect, useRef, useState, useCallback } from "react";

import ReactDOM from "react-dom";
import { twMerge } from "tailwind-merge";

import makeBem from "Utils/makeBem";

const bem = makeBem("FullScreenModal");

interface FullScreenModalProps {
  show?: boolean;
  onHide?: () => void;
  backdrop?: boolean | "static";
  keyboard?: boolean;
  animation?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const FullScreenModal: React.FC<FullScreenModalProps> = ({
  show = false,
  onHide,
  backdrop = true,
  keyboard = true,
  animation = true,
  className,
  children,
}) => {
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const animateInTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animateOutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      animateInTimer.current = setTimeout(() => {
        setIsAnimatingIn(true);
      }, 10);
    } else {
      setIsAnimatingIn(false);
      animateOutTimer.current = setTimeout(
        () => {
          setIsVisible(false);
        },
        animation ? 300 : 0,
      );
    }

    return () => {
      if (animateInTimer.current) clearTimeout(animateInTimer.current);
      if (animateOutTimer.current) clearTimeout(animateOutTimer.current);
    };
  }, [show, animation]);

  useEffect(() => {
    if (!isVisible || !keyboard || !onHide) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onHide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, keyboard, onHide]);

  useEffect(() => {
    if (!isVisible) return;

    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isVisible]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && backdrop !== "static" && onHide) {
        onHide();
      }
    },
    [backdrop, onHide],
  );

  if (!isVisible) return null;

  const themingClass = bem(null);
  const dialogClass = `${themingClass}-dialog`;
  const contentClass = `${themingClass}-content`;
  const modalClasses = twMerge(themingClass, animation && "fade", isAnimatingIn && "in", "block", className);

  const backdropClasses = twMerge("si-modal-backdrop", animation && "fade", isAnimatingIn && "in");

  // Replicate the old structure to maintain compatibility
  // TODO: integrate this with the Cobalt Modal.
  return ReactDOM.createPortal(
    <div role="dialog">
      {backdrop && <div className={backdropClasses} onClick={handleBackdropClick} />}
      <div role="dialog" tabIndex={-1} className={modalClasses} onClick={backdrop === true ? handleBackdropClick : undefined}>
        <div className={dialogClass}>
          <div className={contentClass} role="document">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FullScreenModal;
